import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import './App.css';
import portraitImage from './assets/portrait.jpg';
import workingImage from './assets/onthejob.jpg';
import logo from './assets/vidigitalcodelogo.png';
import outdatedIcon from './assets/outdated-systems-icon.png';
import invisibleIcon from './assets/invisible-icon.png';
import competitionIcon from './assets/competition-icon.png';
import inconsistentIcon from './assets/inconsistent-icon.png';
import localIcon from './assets/local-icon.png';
import overwhelmedIcon from './assets/overwhelmed-icon.png';
import { Link } from 'react-router-dom';



function Home() {
    const [currentSection, setCurrentSection] = useState('landing');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [formStatus, setFormStatus] = useState('');
    const { scrollYProgress } = useScroll();

    // Portfolio data - add your screenshot URLs here
    const portfolioItems = [
        {
            title: "Island Hype",
            type: "Wedding & Portrait Photography",
            description: "Professional photography portfolio showcasing USVI wedding and portrait services across St. Thomas, St. John, and St. Croix.",
            url: "https://islandhype.app/",
            thumbnail: require('./assets/portfolio/islandhype.png'),
            color: '#E16C96'
        },
        {
            title: "The Pelican Post VI",
            type: "Mail & Business Services",
            description: "Complete business website for a Virgin Islands mail and business services center, featuring service listings and contact information.",
            url: "https://akouviyk.github.io/thepelicanpostvi",
            thumbnail: require('./assets/portfolio/pelican.png'),
            color: '#BC4B51'
        },
        {
            title: "GiGi Hair Braiding",
            type: "Hair Salon",
            description: "Modern salon website featuring services, booking system, and gallery for a professional braiding business in Irvington, NJ.",
            url: "https://gigihairbraid.com/",
            thumbnail: require('./assets/portfolio/gigi.png'),
            color: '#95B9C7'
        },
        {
            title: "Bakey Bakey VI",
            type: "Bakery",
            description: "Delightful bakery website with online ordering, menu showcase, and custom cake gallery for a local Virgin Islands bakery.",
            url: "https://bakeybakeyvi.com/",
            thumbnail: require('./assets/portfolio/bakey.png'),
            color: '#F4A259'
        },
        {
            title: "Tigre Salvaje",
            type: "Creative Project",
            description: "Bold and artistic web experience showcasing creative design and interactive storytelling capabilities.",
            url: "https://akouviyk.github.io/tigresalvaje/",
            thumbnail: require('./assets/portfolio/tigre.png'),
            color: '#6B9080'
        },

    ];

    // Parallax effects
    const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
    const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener('mousemove', handleMouseMove);

        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('sending');

        try {
            const response = await fetch('https://formspree.io/f/mblqplkn', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    message: formData.message
                })
            });

            if (response.ok) {
                setFormStatus('success');
                setFormData({ name: '', email: '', message: '' });
                setTimeout(() => setFormStatus(''), 5000);
            } else {
                setFormStatus('error');
                setTimeout(() => setFormStatus(''), 5000);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            setFormStatus('error');
            setTimeout(() => setFormStatus(''), 5000);
        }
    };

    const works = [
        {
            id: 1,
            title: "Build or Revamp Your Website",
            medium: "Website Services",
            story: "Whether you need a brand-new website or want to make your current one look fresh and modern, I'll create something simple and easy to use.",
            color: "#E16C96"
        },
        {
            id: 2,
            title: "Property Photos & Listing Help",
            medium: "Rental Support",
            story: "I'll take phone-quality photos of your property and help set up your Airbnb or VRBO listing with calendar syncing and booking management.",
            color: "#95B9C7"
        },
        {
            id: 3,
            title: "Email Setup Made Simple",
            medium: "Email Services",
            story: "Get a professional email address and have all your messages go to one easy inbox. No more juggling multiple accounts.",
            color: "#F4A259"
        },
        {
            id: 4,
            title: "Social Media Help",
            medium: "Content & Strategy",
            story: "Not sure what to post or when? I'll help you create content, schedule posts, and connect with your audience on Facebook and Instagram.",
            color: "#6B9080"
        },
        {
            id: 5,
            title: "Get Found on Google",
            medium: "Local Visibility",
            story: "I'll set up your Google Business Profile so locals and tourists can find you easily when they search for what you offer.",
            color: "#BC4B51"
        },
        {
            id: 6,
            title: "One-on-One Help",
            medium: "Personal Guidance",
            story: "I'll create easy printed guides and sit down with you to show you how everything works. Learn at your own pace with someone patient.",
            color: "#8E7DBE"
        }
    ];

    return (
        <div className="app">
            {/* Custom Cursor */}
            <motion.div
                className="custom-cursor"
                animate={{
                    x: cursorPosition.x - 10,
                    y: cursorPosition.y - 10
                }}
                transition={{ type: "spring", damping: 30, stiffness: 200 }}
            />

            {/* Floating Navigation */}
            <motion.nav
                className="nav"
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.5, type: "spring" }}
            >
                <motion.div
                    className="nav-logo"
                    whileHover={{ scale: 1.05 }}
                >
                    <img src={logo} alt="VI Logo" className="nav-logo-icon" />
                    <span className="nav-logo-text">Idigital</span>
                </motion.div>

                <div className="nav-links">
                    {['What I Do', 'About Me', 'How It Works',].map((item, i) => (
                        <motion.button
                            key={item}
                            onClick={() => scrollToSection(item.toLowerCase().replace(/ /g, '-'))}
                            className="nav-link"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 + i * 0.1 }}
                            whileHover={{ y: -2 }}
                        >
                            {item}
                        </motion.button>
                    ))}
                </div>
                <Link to="/faq" className="nav-link">
                    FAQ
                </Link>

                <motion.a
                    href="#get-in-touch"
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToSection('get-in-touch');
                    }}
                    className="nav-cta"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Book a Call
                </motion.a>
            </motion.nav>

            {/* 1. LANDING - The Moment of Entry */}
            <section className="landing">
                <motion.div className="bg-gradient" style={{ y: y1 }} />
                <motion.div className="bg-texture" style={{ y: y2 }} />

                <motion.div
                    className="landing-content"
                    style={{ opacity }}
                >
                    <motion.h1
                        className="landing-title"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, delay: 0.3 }}
                    >
                        Let's Build Something
                        <br />
                        <span className="gradient-text">Great Together</span>
                    </motion.h1>

                    <motion.p
                        className="landing-subtitle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1 }}
                    >
                        Simple, worry-free digital support for your small or large business needs
                    </motion.p>

                    <motion.div
                        className="scroll-hint"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2, duration: 1 }}
                    >
                        <motion.div
                            className="scroll-line"
                            animate={{ scaleY: [1, 1.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                    </motion.div>
                </motion.div>

                {/* Ambient particles */}
                <div className="particles">
                    {[...Array(20)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="particle"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                            }}
                            animate={{
                                y: [0, -30, 0],
                                opacity: [0.2, 0.5, 0.2],
                            }}
                            transition={{
                                duration: 3 + Math.random() * 2,
                                repeat: Infinity,
                                delay: Math.random() * 2,
                            }}
                        />
                    ))}
                </div>
            </section>

            {/* 2. GALLERY / WORK SHOWCASE */}
            <section id="what-i-do" className="gallery">
                <motion.div
                    className="section-intro"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title">What I Can Do For You</h2>
                    <p className="section-description">
                        From websites to property photos, tech help that just works.
                    </p>
                </motion.div>

                <div className="works-grid">
                    {works.map((work, index) => (
                        <motion.div
                            key={work.id}
                            className="work-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        >
                            <motion.div
                                className="work-image"
                                style={{ backgroundColor: work.color }}
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="work-overlay">
                                    <div>
                                        <h3>{work.title}</h3>
                                        <p className="work-medium">{work.medium}</p>
                                    </div>
                                </div>
                            </motion.div>

                            <div className="work-info">
                                <p className="work-story">{work.story}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* PORTFOLIO SECTION - Recent Work */}
            <section id="portfolio" className="portfolio">
                <motion.div
                    className="section-intro"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title">Recent Projects</h2>

                    <p className="portfolio-note">
                        * Corporate work not listed here
                    </p>
                </motion.div>

                <div className="portfolio-grid">
                    {portfolioItems.map((item, index) => (
                        <motion.a
                            key={item.title}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="portfolio-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 + 0.1, duration: 0.6 }}
                            whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        >
                            <div
                                className="portfolio-preview"
                                style={{
                                    backgroundImage: item.thumbnail ? `url(${item.thumbnail})` : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundColor: item.color
                                }}
                            >
                                <div className="portfolio-overlay">
                                    <span className="visit-link">Visit Site →</span>
                                </div>
                            </div>
                            <div className="portfolio-info">
                                <h3>{item.title}</h3>
                                <p className="portfolio-type">{item.type}</p>
                                <p className="portfolio-description">
                                    {item.description}
                                </p>
                            </div>
                        </motion.a>
                    ))}
                </div>
            </section>

            {/* 3. THE ARTIST / BIO */}
            <section id="about-me" className="artist">
                <div className="artist-content">
                    <motion.div
                        className="artist-image"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <div className="portrait-frame">
                            <motion.div
                                className="portrait"
                                style={{ backgroundImage: `url(${portraitImage})` }}
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.5 }}
                            />
                            <div className="texture-overlay" />
                        </div>
                    </motion.div>

                    <motion.div
                        className="artist-text"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.2 }}
                    >
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            Hey, I'm Akouvi
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                        >
                            I help people in the US Virgin Islands with simple, worry-free digital support—from websites
                            and email setup to property photos, listing help, and general tech fixes. My goal is to make
                            technology easy, so you can focus on running your business.
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 }}
                        >
                            I understand that technology can feel overwhelming, especially when you're busy managing a property
                            or running a business. Many of my clients are older residents, busy property owners in a tourism-driven
                            economy, or folks dealing with slow internet in some areas. You need digital tools that simply work—and
                            someone who explains things in plain language, not tech-speak.
                        </motion.p>

                        <motion.div
                            className="quote"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.5 }}
                        >
                            <p>"Technology should work for you, not the other way around."</p>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6 }}
                        >
                            What I really enjoy is getting to know you and your business, then building solutions that
                            actually fit your needs. No pushy sales pitches or one-size-fits-all packages. Just honest
                            conversations about what will work for you, your budget, and your goals. Whether you need
                            a website, help with your rental property listings, or just someone to fix that one annoying
                            tech problem—I'm here to help.
                        </motion.p>
                    </motion.div>
                </div>
            </section>


            <section className="pain-points">
                <motion.div
                    className="pain-points-container"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title">Are You Struggling with tech?</h2>


                    <div className="pain-points-grid">
                        {[
                            {
                                icon: outdatedIcon,
                                title: "Outdated Online Presence",
                                description: "Your website looks like it's from 2010, or you don't have one at all",
                                color: "#E16C96"
                            },
                            {
                                icon: invisibleIcon,
                                title: "Invisible to Tourists",
                                description: "Visitors search online before arriving, but they can't find your business",
                                color: "#95B9C7"
                            },
                            {
                                icon: competitionIcon,
                                title: "Looking Local, Not World-Class",
                                description: "You want to compete with mainland U.S. businesses but your branding doesn't reflect your quality",
                                color: "#F4A259"
                            },
                            {
                                icon: inconsistentIcon,
                                title: "Inconsistent Marketing",
                                description: "Social media posts happen... sometimes. When you remember. If you have time.",
                                color: "#6B9080"
                            },
                            {
                                icon: localIcon,
                                title: "Rising Competition",
                                description: "Post-COVID tourism is back, and so are new competitors with slick digital presences",
                                color: "#BC4B51"
                            },
                            {
                                icon: overwhelmedIcon,
                                title: "Tech Overwhelm",
                                description: "You know you need help, but where do you even start? It all feels too complicated.",
                                color: "#8E7DBE"
                            }
                        ].map((pain, index) => (
                            <motion.div
                                key={index}
                                className="pain-card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1, duration: 0.6 }}
                                whileHover={{
                                    y: -8,
                                    scale: 1.02,
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <motion.div
                                    className="pain-icon-wrapper"
                                    style={{ backgroundColor: pain.color }}
                                    whileHover={{ rotate: 5, scale: 1.1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <img src={pain.icon} alt={pain.title} className="pain-icon" />
                                </motion.div>
                                <h3 className="pain-title">{pain.title}</h3>
                                <p className="pain-description">{pain.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        className="pain-cta"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.8 }}
                    >
                        <p className="pain-points-intro">
                            You're not alone. Many businesses ive worked with also faced these same challenges.
                        </p>
                        <motion.button
                            onClick={() => scrollToSection('get-in-touch')}
                            className="pain-cta-btn"
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            I Can Help →
                        </motion.button>
                    </motion.div>
                </motion.div>
            </section>

            {/* USVI-SPECIFIC SECTION */}
            <section className="usvi-focus">
                <motion.div
                    className="usvi-container"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title">Why This Matters in the USVI</h2>

                    <div className="usvi-grid">
                        <motion.div
                            className="usvi-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            <h3>For Property Owners</h3>
                            <p>
                                You're busy managing rentals, dealing with guests, and keeping everything running smoothly.
                                The last thing you need is to spend hours figuring out Airbnb settings or why your website
                                isn't working. I handle all the tech stuff so you can focus on hosting.
                            </p>
                        </motion.div>

                        <motion.div
                            className="usvi-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                        >
                            <h3>For Small Businesses</h3>
                            <p>
                                In a tourism-driven economy, your online presence matters. Visitors search for local
                                businesses before they arrive. I'll make sure they can find you, see what you offer,
                                and easily get in touch—all without breaking the bank.
                            </p>
                        </motion.div>

                        <motion.div
                            className="usvi-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 }}
                        >
                            <h3>Understanding The Community</h3>
                            <p>
                                I know many folks here prefer face-to-face conversations and step-by-step guidance.
                                Some areas have slower internet, and not everyone grew up with technology. That's exactly
                                why I offer in-person training, printed guides, and patient, judgment-free support.
                            </p>
                        </motion.div>
                    </div>







                </motion.div>
            </section>

            {/* TRANSITION: The Work - Visual Proof */}
            <section className="work-proof">
                <motion.div
                    className="work-proof-container"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                >
                    <motion.div
                        className="work-proof-image"
                        initial={{ scale: 1.1, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                        style={{ backgroundImage: `url(${workingImage})` }}
                    >
                        <div className="work-proof-overlay">
                            <motion.div
                                className="work-proof-text"
                                initial={{ y: 30, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3, duration: 0.8 }}
                            >
                                <h3>Building Solutions, One Line at a Time</h3>
                                <p>Every project gets my full attention and care</p>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            </section>

            {/* 4. PROCESS SECTION */}
            <section id="how-it-works" className="process">
                <motion.div
                    className="section-intro"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="section-title">How We'll Work Together</h2>
                    <p className="section-description">
                        Simple, straightforward, and stress-free—the way it should be
                    </p>
                </motion.div>

                <div className="process-timeline">
                    {[
                        {
                            title: "Let's Talk",
                            description: "We'll chat about your business, what you're hoping to achieve, and what's working (or not working) for you right now. No pressure, just conversation."
                        },
                        {
                            title: "I'll Make a Plan",
                            description: "Based on our chat, I'll put together a clear plan and quote. You'll know exactly what you're getting and what it'll cost before we start."
                        },
                        {
                            title: "Build & Check In",
                            description: "I'll get to work building your site or project. We'll check in regularly so you can see progress and make sure we're on the right track."
                        },
                        {
                            title: "Polish & Perfect",
                            description: "Before we launch, I'll test everything thoroughly, make sure it works great on phones and computers, and get it running fast."
                        },
                        {
                            title: "Launch & Support",
                            description: "Time to go live! I'll show you how everything works and stick around to help if you need anything. You're never on your own."
                        }
                    ].map((step, index) => (
                        <motion.div
                            key={index}
                            className="process-step"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2, duration: 0.6 }}
                        >
                            <motion.div
                                className="step-number"
                                whileHover={{ scale: 1.2, rotate: 5 }}
                            >
                                {index + 1}
                            </motion.div>
                            <div className="step-content">
                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* 5. CONTACT / COMMISSIONS */}
            <section id="get-in-touch" className="contact">
                <motion.div
                    className="contact-container"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <h2 className="section-title">Let's Chat About Your Project</h2>
                    <motion.div
                        className="cta-box"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                    >

                        <p>Not sure where to start? Let's talk! I'll explain everything in plain language, answer your
                            questions, and help you figure out what makes sense for your situation. No pressure, no tech jargon.</p>

                        <h3>Book a Free 15-Minute Call</h3>

                    </motion.div>

                    <motion.form
                        className="contact-form"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        onSubmit={handleSubmit}
                    >
                        <div className="form-group">
                            <label>Your Name</label>
                            <motion.input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                required
                                whileFocus={{ scale: 1.02 }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <motion.input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleFormChange}
                                required
                                whileFocus={{ scale: 1.02 }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>

                        <div className="form-group full-width">
                            <label>What's on your mind?</label>
                            <motion.textarea
                                rows="6"
                                name="message"
                                value={formData.message}
                                onChange={handleFormChange}
                                required
                                whileFocus={{ scale: 1.02 }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>

                        <motion.button
                            type="submit"
                            className="submit-btn"
                            disabled={formStatus === 'sending'}
                            whileHover={{ scale: formStatus === 'sending' ? 1 : 1.05, y: formStatus === 'sending' ? 0 : -2 }}
                            whileTap={{ scale: formStatus === 'sending' ? 1 : 0.95 }}
                        >
                            {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                        </motion.button>

                        {formStatus === 'success' && (
                            <motion.p
                                className="form-message success"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                ✓ Message sent! I'll get back to you soon.
                            </motion.p>
                        )}

                        {formStatus === 'error' && (
                            <motion.p
                                className="form-message error"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                ✗ Oops! Something went wrong. Please try again.
                            </motion.p>
                        )}
                    </motion.form>

                    <div className="social-links">
                        {['LinkedIn'].map((platform, i) => (
                            <motion.a
                                key={platform}
                                href="https://www.linkedin.com/in/akouvik"
                                target="_blank"
                                rel="noopener noreferrer"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 + i * 0.1 }}
                                whileHover={{ y: -3, color: '#E16C96' }}
                            >
                                {platform}
                            </motion.a>
                        ))}
                    </div>
                </motion.div>

                {/* Coffee Section - Optimal Positioning */}
                <motion.div
                    className="coffee-section-contact"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                >
                    <p className="coffee-text">
                        Not ready to start a project yet? If you've enjoyed exploring my work and want to fuel the next creative build... buy me a coffee ☕
                    </p>
                    <motion.a
                        href="https://paypal.me/akoovee"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="coffee-btn"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        ☕ Buy Me a Coffee
                    </motion.a>
                </motion.div>
            </section>

            {/* FOOTER */}
            <footer className="footer">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    <p>© 2025 VIdigital • Made with care in the Virgin Islands 🌴</p>
                    <p className="footer-quote">Simple, worry-free digital support for your small or large business needs</p>
                </motion.div>
            </footer>
        </div>
    );
}

export default Home;
